import { useEffect, useRef, useState } from 'react';

const problems = [
  { icon: 'ri-bill-line', text: 'Las cuentas llegan cada mes y el dinero nunca alcanza' },
  { icon: 'ri-arrow-up-line', text: 'Los gastos parecen aumentar sin que sepas exactamente por qué' },
  { icon: 'ri-question-line', text: 'El dinero desaparece sin saber exactamente en qué se fue' },
  { icon: 'ri-emotion-sad-line', text: 'Aparecen discusiones y estrés por temas económicos' },
  { icon: 'ri-alarm-warning-line', text: 'Preocupación constante por el futuro financiero de tu familia' },
  { icon: 'ri-bank-card-line', text: 'Deudas que no desaparecen y que generan ansiedad mes a mes' },
];

export default function ProblemaSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-stone-950 py-20 md:py-28 px-6 md:px-12" ref={ref}>
      <div className="max-w-5xl mx-auto">
        <div
          className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="inline-block text-amber-400 text-xs font-bold tracking-widest uppercase mb-4">
            ¿Te suena familiar?
          </span>
          <h2
            className="text-3xl md:text-5xl font-bold text-white leading-tight mb-5"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            El dinero se convierte en una fuente{' '}
            <span className="text-amber-400">constante de preocupación</span>
          </h2>
          <p className="text-stone-400 text-lg max-w-2xl mx-auto">
            En muchas familias la situación financiera genera tensión. No es culpa tuya, es falta de un sistema claro.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {problems.map((p, i) => (
            <div
              key={i}
              className={`border border-stone-800 rounded-xl p-6 flex items-start gap-4 transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-900/40 flex-shrink-0">
                <i className={`${p.icon} text-red-400 text-lg`} />
              </div>
              <p className="text-stone-300 text-sm leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>

        <div
          className={`mt-14 rounded-2xl border border-amber-400/20 bg-gradient-to-r from-amber-950/40 to-emerald-950/40 p-8 md:p-10 text-center transition-all duration-700 delay-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p
            className="text-white text-xl md:text-2xl font-semibold leading-relaxed"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            "La estabilidad financiera no depende solo de cuánto se gana.
            Depende de cómo se <span className="text-amber-400">organiza, administra y planifica</span> el dinero en el hogar."
          </p>
        </div>
      </div>
    </section>
  );
}
